/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useState } from "react"
import {
  CONFIRMED_BPM_MAX,
  CONFIRMED_BPM_MIN,
  PcgAcceptedDomains,
  PcgAcousticQuality,
  PcgDispositionDecision,
  PcgDispositionRequest,
  PcgObservationFinding,
  PcgRecordingDisposition,
  PcgReviewerObservations,
  dispositionHasReviewerContent,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"
import {
  EMPTY_OBSERVATIONS,
  HCP_ACOUSTIC_RATING_LABELS,
  OBSERVATION_FINDING_LABELS,
  OBSERVATION_PATTERN_GROUPS,
  OBSERVATION_PATTERN_LABELS,
  OTHER_ACOUSTIC_OBSERVATION_LABELS,
  RHYTHM_OBSERVATION_LABELS,
} from "./reviewer-observations"
import { AcceptEvidence } from "./accept-evidence"

/**
 * Accept / Reject / Note controls of the "Recording Review" row (handoff
 * v0.8.3.1 `.exam-review-row`, styled by pcg-exam-workspace.css):
 * - Accept opens the evidence dialog: the reviewer chooses which evidence of
 *   this recording — Timing, Structural Sound, or both — is suitable for the
 *   Medaica Report. Both start clear on first open so the choice is deliberate,
 *   at least one is required, a domain the recording cannot support is
 *   disabled, Cancel changes nothing, and an existing acceptance can be
 *   changed or cleared. Other recordings do not have to be accepted.
 * - Reject is toggleable; toggling it off returns the recording to
 *   no-decision (reviewer observations may remain). Reject takes no
 *   reasons, as upstream; reasons stored by earlier versions are kept
 *   unchanged on later saves.
 * - Note opens the structured Reviewer Observations dialog (v0.8.3.1
 *   `recordingNoteBackdrop`, the first section from Medaica Viewer v0.9.1):
 *   the reviewer's acoustic rating and confirmed BPM (a whole number 25–220,
 *   checked here and again by the server), a primary screening observation,
 *   an observed pattern revealed for "Murmur suspected", other-acoustic and
 *   rhythm checkboxes, an optional internal review note and an optional
 *   Report note (Medaica Viewer v0.9.1 at pcg-viewer-mvp git 20ac6e4) —
 *   saved together onto the disposition row. The selections and the internal
 *   note are HCP review context; only the Report note reaches the patient and
 *   provider reports. A rating, a confirmed BPM or a Report note alone is
 *   reviewer content.
 * Every change is a whole-state PUT (append-only history server-side).
 *
 * Rendered into the viewer's `reviewControls` slot, so the note summary is a
 * full-width flex item of the same review row; the dialog is a fixed-position overlay like the timing-review dialog.
 */
const decisionTitle = (label: string, active: boolean): string =>
  active ? `${label} selected. Click again to clear.` : `${label} this recording.`

const DispositionControls = ({
  disposition,
  recordingLabel,
  acceptEvidence = null,
  onSubmit,
  busy,
}: {
  disposition: PcgRecordingDisposition | null
  /** Site label of the selected recording, shown in the dialog title. */
  recordingLabel?: string
  /**
   * Which evidence domains the selected recording can contribute, with the
   * values shown in the Accept dialog; null = nothing to accept (the
   * recording has no analysis results).
   */
  acceptEvidence?: AcceptEvidence | null
  onSubmit: (request: PcgDispositionRequest) => void
  busy: boolean
}): ReactElement => {
  const decision: PcgDispositionDecision = disposition?.decision ?? "none"
  const rejectReasons = disposition?.rejectReasons ?? []
  const note = disposition?.note ?? ""
  const observations = disposition?.observations ?? null

  const [dialogOpen, setDialogOpen] = useState(false)
  const [acceptOpen, setAcceptOpen] = useState(false)
  const [domainsDraft, setDomainsDraft] = useState<PcgAcceptedDomains>({ timing: false, structuralSound: false })
  const [noteDraft, setNoteDraft] = useState(note)
  const [reportNoteDraft, setReportNoteDraft] = useState(observations?.reportNote ?? "")
  const [observationsDraft, setObservationsDraft] = useState<PcgReviewerObservations>(
    observations ?? EMPTY_OBSERVATIONS
  )
  // The BPM field is edited as text so a partial entry is not coerced; it is
  // validated on save
  const confirmedBpmText = (value: number | null | undefined) =>
    value === null || value === undefined ? "" : String(value)
  const [bpmDraft, setBpmDraft] = useState(confirmedBpmText(observations?.confirmedBpm))
  const [bpmError, setBpmError] = useState<string | null>(null)

  // Re-sync the drafts when the selected recording (and thus disposition) changes
  useEffect(() => {
    setNoteDraft(note)
    setReportNoteDraft(observations?.reportNote ?? "")
    setObservationsDraft(observations ?? EMPTY_OBSERVATIONS)
    setBpmDraft(confirmedBpmText(observations?.confirmedBpm))
    setBpmError(null)
    setDialogOpen(false)
    setAcceptOpen(false)
  }, [disposition?.auscultationId, note, observations])

  // Escape closes either dialog without saving (handoff keydown rule)
  useEffect(() => {
    if (!dialogOpen && !acceptOpen) {
      return undefined
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDialogOpen(false)
        setAcceptOpen(false)
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [dialogOpen, acceptOpen])

  // Every change is a whole-state PUT, so a note edit must carry the
  // accepted domains along or it would reset them
  const submit = (partial: Partial<PcgDispositionRequest>) => {
    onSubmit({
      decision,
      rejectReasons,
      note,
      observations,
      acceptedDomains: decision === "accepted" ? disposition?.acceptedDomains ?? null : null,
      ...partial,
    })
  }

  const toggleReject = () => {
    const next = decision === "rejected" ? "none" : "rejected"
    submit({ decision: next, rejectReasons: next === "rejected" ? rejectReasons : [], acceptedDomains: null })
  }

  // An accepted row saved before domains existed counts for both
  const currentDomains: PcgAcceptedDomains =
    decision === "accepted"
      ? disposition?.acceptedDomains ?? { timing: true, structuralSound: true }
      : { timing: false, structuralSound: false }

  const openAccept = () => {
    setDomainsDraft(currentDomains)
    setAcceptOpen(true)
  }

  const saveAccept = () => {
    if (!domainsDraft.timing && !domainsDraft.structuralSound) {
      return
    }
    submit({ decision: "accepted", rejectReasons: [], acceptedDomains: domainsDraft })
    setAcceptOpen(false)
  }

  const clearAccept = () => {
    submit({ decision: "none", rejectReasons: [], acceptedDomains: null })
    setAcceptOpen(false)
  }

  const openDialog = () => {
    setNoteDraft(note)
    setReportNoteDraft(observations?.reportNote ?? "")
    setObservationsDraft(observations ?? EMPTY_OBSERVATIONS)
    setBpmDraft(confirmedBpmText(observations?.confirmedBpm))
    setBpmError(null)
    setDialogOpen(true)
  }

  // A pattern is only meaningful with "Murmur suspected" (the section is
  // hidden and its selection dropped otherwise — handoff
  // renderRecordingPatternVisibility; the server applies the same rule).
  const setFinding = (finding: PcgObservationFinding) => {
    setObservationsDraft((draft) => ({
      ...draft,
      finding,
      pattern: finding === "murmur_suspected" ? draft.pattern : null,
    }))
  }

  const saveDialog = () => {
    const trimmed = noteDraft.trim()
    const bpmText = bpmDraft.trim()
    const confirmedBpm = bpmText === "" ? null : Number(bpmText)
    if (
      confirmedBpm !== null &&
      !(Number.isInteger(confirmedBpm) && confirmedBpm >= CONFIRMED_BPM_MIN && confirmedBpm <= CONFIRMED_BPM_MAX)
    ) {
      setBpmError(`Confirmed BPM must be a whole number from ${CONFIRMED_BPM_MIN} to ${CONFIRMED_BPM_MAX}.`)
      return
    }
    const reportNote = reportNoteDraft.trim()
    const draft: PcgReviewerObservations = { ...observationsDraft, confirmedBpm, reportNote: reportNote || null }
    const hasContent =
      draft.acousticQuality ||
      confirmedBpm !== null ||
      draft.finding ||
      draft.pattern ||
      draft.otherObservations.length > 0 ||
      draft.rhythmObservations.length > 0 ||
      reportNote
    submit({ note: trimmed, observations: hasContent ? draft : null })
    setDialogOpen(false)
  }

  const clearDialog = () => {
    submit({ note: "", observations: null })
    setDialogOpen(false)
  }

  const hasNote = dispositionHasReviewerContent(disposition)
  const domainOptions: { key: keyof PcgAcceptedDomains; label: string }[] = [
    { key: "timing", label: "Timing" },
    { key: "structuralSound", label: "Structural Sound" },
  ]

  return (
    <>
      <div className="exam-review-title">Recording Review</div>
      <button
        type="button"
        className={`exam-review-btn accept${decision === "accepted" ? " active" : ""}`}
        aria-pressed={decision === "accepted"}
        title={
          decision === "accepted"
            ? "Accepted evidence recorded. Click to review or change."
            : "Choose Timing and/or Structural Sound evidence for the Report."
        }
        disabled={busy}
        onClick={openAccept}
      >
        Accept
      </button>
      <button
        type="button"
        className={`exam-review-btn reject${decision === "rejected" ? " active" : ""}`}
        aria-pressed={decision === "rejected"}
        title={decisionTitle("Reject", decision === "rejected")}
        disabled={busy}
        onClick={toggleReject}
      >
        Reject
      </button>
      <button
        type="button"
        className={`exam-review-btn edit${hasNote ? " active" : ""}`}
        aria-pressed={hasNote}
        title={hasNote ? "Reviewer observations recorded. Click to edit or clear." : "Add reviewer observations."}
        disabled={busy}
        onClick={openDialog}
      >
        Note
      </button>

      {acceptOpen && (
        <div
          className="recording-note-backdrop open"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setAcceptOpen(false)
          }}
        >
          <section
            className="recording-note-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="evidenceAcceptTitle"
          >
            <div className="recording-note-head">
              <div className="recording-note-title" id="evidenceAcceptTitle">
                {recordingLabel ? `Accept evidence · ${recordingLabel}` : "Accept evidence from this recording"}
              </div>
              <button
                className="recording-note-close"
                type="button"
                aria-label="Close evidence acceptance"
                onClick={() => setAcceptOpen(false)}
              >
                ×
              </button>
            </div>
            <form
              className="recording-note-body"
              onSubmit={(event) => {
                event.preventDefault()
                saveAccept()
              }}
            >
              <div className="recording-note-guidance">
                Choose the evidence suitable for the Report. At least one selection is required; other recordings do not
                have to be accepted.
              </div>
              <div className="evidence-accept-options">
                {domainOptions.map(({ key, label }) => {
                  const evidence = acceptEvidence?.[key]
                  const available = Boolean(evidence?.available)
                  return (
                    <label
                      key={key}
                      className={`evidence-accept-option${domainsDraft[key] ? " checked" : ""}${
                        available ? "" : " unavailable"
                      }`}
                    >
                      <input
                        type="checkbox"
                        aria-label={label}
                        checked={domainsDraft[key]}
                        disabled={!available}
                        onChange={(event) => setDomainsDraft((draft) => ({ ...draft, [key]: event.target.checked }))}
                      />
                      <span>
                        <span className="evidence-accept-domain">{label}</span>
                        <span className="evidence-accept-values" data-testid={`accept-values-${key}`}>
                          {evidence?.text ?? "Not available from this recording"}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </div>
              <div className="recording-note-actions">
                {decision === "accepted" && (
                  <button className="recording-note-action danger" type="button" disabled={busy} onClick={clearAccept}>
                    Clear acceptance
                  </button>
                )}
                <button className="recording-note-action" type="button" onClick={() => setAcceptOpen(false)}>
                  Cancel
                </button>
                <button
                  className="recording-note-action primary"
                  type="submit"
                  disabled={busy || (!domainsDraft.timing && !domainsDraft.structuralSound)}
                >
                  Accept selected evidence
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {dialogOpen && (
        <div
          className="recording-note-backdrop open"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setDialogOpen(false)
          }}
        >
          <section
            className="recording-note-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="recordingNoteTitle"
          >
            <div className="recording-note-head">
              <div className="recording-note-title" id="recordingNoteTitle">
                {recordingLabel ? `Reviewer observations · ${recordingLabel}` : "Reviewer observations"}
              </div>
              <button
                className="recording-note-close"
                type="button"
                aria-label="Close note"
                onClick={() => setDialogOpen(false)}
              >
                ×
              </button>
            </div>
            <form
              className="recording-note-body"
              onSubmit={(event) => {
                event.preventDefault()
                saveDialog()
              }}
            >
              <div className="recording-note-guidance">
                <strong>Reviewer observations are internal by default.</strong> They remain available to the reviewing
                HCP but are not shown in the patient or provider report unless entered as a Report note below.
              </div>
              <section className="recording-note-section">
                <div className="recording-note-label">HCP acoustic rating</div>
                <div className="hcp-rating-row" role="radiogroup" aria-label="HCP acoustic rating">
                  {(Object.keys(HCP_ACOUSTIC_RATING_LABELS) as PcgAcousticQuality[]).map((value) => (
                    <label className="recording-note-option hcp-rating-btn" key={value}>
                      <input
                        type="radio"
                        name="hcpAcousticRating"
                        value={value}
                        checked={observationsDraft.acousticQuality === value}
                        onChange={() => setObservationsDraft((draft) => ({ ...draft, acousticQuality: value }))}
                      />
                      {HCP_ACOUSTIC_RATING_LABELS[value]}
                    </label>
                  ))}
                </div>
                <label className="hcp-bpm-field" htmlFor="hcpConfirmedBpmNote">
                  Confirmed BPM{" "}
                  <input
                    id="hcpConfirmedBpmNote"
                    type="number"
                    min={CONFIRMED_BPM_MIN}
                    max={CONFIRMED_BPM_MAX}
                    step={1}
                    inputMode="numeric"
                    placeholder="—"
                    value={bpmDraft}
                    aria-invalid={bpmError ? true : undefined}
                    onChange={(event) => {
                      setBpmDraft(event.target.value)
                      setBpmError(null)
                    }}
                  />
                </label>
                {bpmError && (
                  <div className="recording-note-error" role="alert">
                    {bpmError}
                  </div>
                )}
              </section>
              <section className="recording-note-section">
                <div className="recording-note-label">Primary screening observation</div>
                <div className="recording-note-primary" role="radiogroup" aria-label="Acoustic finding">
                  {(Object.keys(OBSERVATION_FINDING_LABELS) as PcgObservationFinding[]).map((value) => (
                    <label className="recording-note-option" key={value}>
                      <input
                        type="radio"
                        name="recordingFinding"
                        value={value}
                        checked={observationsDraft.finding === value}
                        onChange={() => setFinding(value)}
                      />
                      {OBSERVATION_FINDING_LABELS[value]}
                    </label>
                  ))}
                </div>
              </section>
              {observationsDraft.finding === "murmur_suspected" && (
                <section className="recording-note-section" data-testid="observation-pattern-section">
                  <div className="recording-note-label">Observed pattern (optional)</div>
                  <div className="recording-note-grid">
                    {OBSERVATION_PATTERN_GROUPS.map((group) => (
                      <fieldset className="recording-note-group" key={group.legend}>
                        <legend>{group.legend}</legend>
                        {group.options.map((value) => (
                          <label className="recording-note-option" key={value}>
                            <input
                              type="radio"
                              name="recordingPattern"
                              value={value}
                              checked={observationsDraft.pattern === value}
                              onChange={() => setObservationsDraft((draft) => ({ ...draft, pattern: value }))}
                            />
                            {OBSERVATION_PATTERN_LABELS[value]}
                          </label>
                        ))}
                      </fieldset>
                    ))}
                  </div>
                </section>
              )}
              <section className="recording-note-section">
                <div className="recording-note-label">Other acoustic observation (optional)</div>
                <div className="recording-note-grid">
                  {(
                    Object.keys(OTHER_ACOUSTIC_OBSERVATION_LABELS) as (keyof typeof OTHER_ACOUSTIC_OBSERVATION_LABELS)[]
                  ).map((value) => (
                    <label className="recording-note-option" key={value}>
                      <input
                        type="checkbox"
                        name="recordingOther"
                        value={value}
                        checked={observationsDraft.otherObservations.includes(value)}
                        onChange={() =>
                          setObservationsDraft((draft) => ({
                            ...draft,
                            otherObservations: draft.otherObservations.includes(value)
                              ? draft.otherObservations.filter((item) => item !== value)
                              : [...draft.otherObservations, value],
                          }))
                        }
                      />
                      {OTHER_ACOUSTIC_OBSERVATION_LABELS[value]}
                    </label>
                  ))}
                </div>
              </section>
              <section className="recording-note-section">
                <div className="recording-note-label">Rhythm observation (optional)</div>
                <div className="recording-note-grid">
                  {(Object.keys(RHYTHM_OBSERVATION_LABELS) as (keyof typeof RHYTHM_OBSERVATION_LABELS)[]).map(
                    (value) => (
                      <label className="recording-note-option" key={value}>
                        <input
                          type="checkbox"
                          name="recordingRhythm"
                          value={value}
                          checked={observationsDraft.rhythmObservations.includes(value)}
                          onChange={() =>
                            setObservationsDraft((draft) => ({
                              ...draft,
                              rhythmObservations: draft.rhythmObservations.includes(value)
                                ? draft.rhythmObservations.filter((item) => item !== value)
                                : [...draft.rhythmObservations, value],
                            }))
                          }
                        />
                        {RHYTHM_OBSERVATION_LABELS[value]}
                      </label>
                    )
                  )}
                </div>
              </section>
              <section className="recording-note-section">
                <label className="recording-note-label" htmlFor="recordingNoteText">
                  Internal review note (optional)
                </label>
                <div className="recording-note-helper">
                  Saved with this recording for HCP review and audit. Not shown in the patient or provider report.
                </div>
                <textarea
                  className="recording-note-text"
                  id="recordingNoteText"
                  placeholder="Add internal review context"
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                />
              </section>
              <section className="recording-note-section">
                <label className="recording-note-label" htmlFor="recordingReportNote">
                  Report note (optional)
                </label>
                <div className="recording-note-helper">
                  Included in Medaica Report and visible to the patient and their care team.
                </div>
                <textarea
                  className="recording-note-text"
                  id="recordingReportNote"
                  placeholder="Add report-facing wording only when useful"
                  value={reportNoteDraft}
                  onChange={(event) => setReportNoteDraft(event.target.value)}
                />
              </section>
              <div className="recording-note-actions">
                <button className="recording-note-action" type="button" disabled={busy} onClick={clearDialog}>
                  Clear
                </button>
                <button className="recording-note-action" type="button" onClick={() => setDialogOpen(false)}>
                  Cancel
                </button>
                <button className="recording-note-action primary" type="submit" disabled={busy}>
                  Save note
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </>
  )
}

export default DispositionControls
